// Vendor
import { FC, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

// Components
import Button, { ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { Row } from 'src/components/atoms/Row';
import { ControlledDropdown } from 'src/components/molecules/Dropdown';
import { DropdownMulticheck } from 'src/components/molecules/DropdownMulticheck';
import { Container, ContentCheck } from './styles';

// Constants
import {
  reasons as insufficientReasonTypes,
  olvInsufficientReasons,
  reasonsEditedExtended as reasonsEditedExtendedType,
  results as resultTypes
} from './constants';

// Helpers
import { canSelectDocType } from 'src/features/myWork/helpers';
import { OFFER_LETTER_TYPE } from 'src/components/folders/ModernizedFraudAnalystReview/helpers/proofHelpers';
import { formatCheckboxData } from 'src/helpers';

// Enums
import { EntryResultEnum } from 'src/ts/enums';

// Types
import {
  IProof,
  ProofResultEditedReasonType,
  ProofResultInsufficientReasonType,
  ProofResultType,
  ProofType
} from 'src/ts/interfaces';

// Hooks
import { useProofDocumentTypes } from 'src/features/proofs/hooks';

export type IFormValues = {
  result?: ProofResultType;
  resultEditedReason?: ProofResultEditedReasonType[];
  resultInsufficientReason?: ProofResultInsufficientReasonType;
  olvReasons?: ProofResultInsufficientReasonType[];
  document?: ProofType;
  note: IProof['note'];
};

export type BulkSelectDocProps = {
  onApply: (content: IFormValues) => void;
  isDisabled: boolean;
  markSelectAll: boolean | null;
  onGetCheck?: ({ selectAll }: { selectAll: boolean | null }) => void;
  checkedProofTypes?: ProofType[];
};

const BulkSelectDoc: FC<BulkSelectDocProps> = ({
  onApply,
  isDisabled,
  markSelectAll,
  onGetCheck,
  checkedProofTypes = []
}) => {
  const methods = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      result: undefined,
      resultEditedReason: [],
      resultInsufficientReason: undefined,
      olvReasons: [],
      document: undefined,
      note: ''
    }
  });

  const { result, resultEditedReason, resultInsufficientReason } = methods.watch();

  const { proofDocumentTypes } = useProofDocumentTypes();

  const allCheckedAreOfferLetter =
    checkedProofTypes.length > 0 && checkedProofTypes.every((t) => t === OFFER_LETTER_TYPE);
  const hasMixedTypes =
    checkedProofTypes.length > 0 &&
    checkedProofTypes.some((t) => t === OFFER_LETTER_TYPE) &&
    !allCheckedAreOfferLetter;
  const showOlvDropdown = allCheckedAreOfferLetter && result === EntryResultEnum.Undetermined;

  // Reset incompatible reason fields when dropdown mode changes (F-1: proof selection changes)
  const { resetField } = methods;
  useEffect(() => {
    if (showOlvDropdown || hasMixedTypes) {
      resetField('resultInsufficientReason');
    }
    if (!showOlvDropdown) {
      resetField('olvReasons');
    }
  }, [showOlvDropdown, hasMixedTypes, resetField]);

  const olvCheckboxData = useMemo(
    () => formatCheckboxData({ data: olvInsufficientReasons, value: 'value', label: 'label' }),
    []
  );

  const handleSubmit = (formData: IFormValues) => {
    onApply(formData);
  };

  const onResultChange = (data: { result: { selected: { value: EntryResultEnum } } }) => {
    const selection = data.result.selected.value;
    if (selection === EntryResultEnum.Undetermined) {
      methods.resetField('resultEditedReason');
      methods.resetField('olvReasons');
    } else if (selection === EntryResultEnum.Edited) {
      methods.resetField('resultInsufficientReason');
      methods.resetField('olvReasons');
    } else {
      methods.resetField('resultInsufficientReason');
      methods.resetField('resultEditedReason');
      methods.resetField('olvReasons');
    }
  };

  return (
    <Container>
      <FormProvider {...methods}>
        <Row alignItems="flex-start" gap={1}>
          <ContentCheck>
            <Checkbox
              name="select-all"
              label="Select all"
              value="all"
              isNull={markSelectAll === null}
              isDisabled={isDisabled}
              isChecked={markSelectAll === null ? false : markSelectAll}
              showLabel={false}
              onClick={({ isChecked }) => {
                onGetCheck?.({ selectAll: isChecked });
              }}
            />
          </ContentCheck>
          <ControlledDropdown
            name="result"
            data={resultTypes}
            placeholder={result ? 'Result' : 'Select Result'}
            showLabel={false}
            isDisabled={isDisabled}
            size="sm"
            config={{ required: 'Complete this field' }}
            onChange={onResultChange}
          />
          {result === EntryResultEnum.Undetermined && showOlvDropdown && (
            <div data-testid="olv-reasons-dropdown">
              <Controller
                name="olvReasons"
                control={methods.control}
                rules={{ required: 'Complete this field' }}
                render={({ field: { name, onChange } }) => (
                  <DropdownMulticheck
                    data={olvCheckboxData}
                    onChange={({ ids }) => {
                      onChange(
                        ids
                          ? (ids.split(',').filter(Boolean) as ProofResultInsufficientReasonType[])
                          : []
                      );
                    }}
                    name={name}
                    placeholder="Select Reasons"
                    size="sm"
                    isDisabled={isDisabled}
                    textSelectAll="All reasons selected"
                  />
                )}
              />
            </div>
          )}
          {result === EntryResultEnum.Undetermined && hasMixedTypes && (
            <div
              data-testid="mixed-types-dropdown"
              title="Uncheck non-Offer Letter proofs to select OLV reasons"
            >
              <ControlledDropdown
                name="resultInsufficientReason"
                data={insufficientReasonTypes}
                placeholder="Mixed document types selected"
                showLabel={false}
                isDisabled={true}
                size="sm"
                config={{}}
              />
            </div>
          )}
          {result === EntryResultEnum.Undetermined && !showOlvDropdown && !hasMixedTypes && (
            <div data-testid="generic-reason-dropdown">
              <ControlledDropdown
                name="resultInsufficientReason"
                data={insufficientReasonTypes}
                placeholder={resultInsufficientReason ? 'Reason' : 'Select a reason'}
                showLabel={false}
                isDisabled={isDisabled}
                size="sm"
                config={{ required: 'Complete this field' }}
              />
            </div>
          )}
          {result === EntryResultEnum.Edited && (
            <Controller
              name="resultEditedReason"
              control={methods.control}
              rules={{ required: 'Complete this field' }}
              render={({ field: { name } }) => (
                <DropdownMulticheck
                  data={formatCheckboxData({
                    data: reasonsEditedExtendedType,
                    value: 'value',
                    label: 'label'
                  })}
                  onChange={({ ids }) => {
                    methods.setValue(
                      'resultEditedReason',
                      ids?.split(',') as ProofResultEditedReasonType[]
                    );
                  }}
                  name={name}
                  placeholder={resultEditedReason ? 'Reason' : 'Select a reason'}
                  size="sm"
                  isDisabled={isDisabled}
                  textSelectAll="All reasons selected"
                />
              )}
            />
          )}
          {canSelectDocType(result, resultInsufficientReason) && (
            <ControlledDropdown
              name="document"
              data={proofDocumentTypes}
              placeholder="Document Type"
              showLabel={false}
              isDisabled={isDisabled}
              size="sm"
              config={{ required: 'Complete this field' }}
            />
          )}

          <Button
            name="update_selected_button"
            variant={ButtonVariant.contained}
            size={ButtonSize.medium}
            isDisabled={!methods.formState.isDirty || markSelectAll === false || isDisabled}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            Update Selected
          </Button>
        </Row>
      </FormProvider>
    </Container>
  );
};

BulkSelectDoc.defaultProps = {
  onApply: (content) => console.log(content),
  isDisabled: false,
  markSelectAll: false
};

export default BulkSelectDoc;
